import { FC, useEffect, useState, useMemo, useRef } from "react"
import { Column, useTable } from "react-table"
import { User } from "../../models/user"
import userService from "../../services/user"
import HelperService from "../../services/helper"
import useNotyf from "../../context/useNotify"
import Dropdown from "../common/Dropdown"
import ReactPaginate from "react-paginate"
import { Meta, QueryParams } from "../../models/misc"
import { StyledPagination, StyledTable, StyledNoDataFound } from "./Styles"
import { useDebounce } from "../../hooks/hooks"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faCrown } from "@fortawesome/free-solid-svg-icons"
import LoadingSpinner from "../common/LoadingSpinner"
import styled from "styled-components"
import placeholderImage from "../../images/common/vgc-logo.png"

const LeaderboardTable: FC = () => {
  const notyf = useNotyf()

  // Data
  const [users, setUsers] = useState<User[]>([])
  const [displayStat, setDisplayStat] = useState<string>("goals")
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true)

  const statOptions = useMemo(() => {
    return [
      { label: "Top Goals", value: "goals" },
      { label: "Top Assists", value: "assists" },
      { label: "Top Passes", value: "passes" },
      { label: "Top Tackles", value: "tackles" },
      { label: "Top Interceptions", value: "interceptions" },
      { label: "Top Headers", value: "headers" },
      { label: "Top Saves", value: "saves" },
      { label: "Top Clean Sheets", value: "cleanSheets" },
        { label: "Top Passes Attempted", value: "passesAttempted" },
        { label: "Top Passes Completed", value: "passesCompleted" },
        { label: "Top Tackles Attempted", value: "tacklesAttempted" },
        { label: "Top Tackles Completed", value: "tacklesCompleted" },
        { label: "Top Match Rating", value: "matchRating" },
        { label: "Top Shots", value: "shots" },
    ]
  }, [])

  // API
  const fetchUsers = async (page: number = 1, stat: string = displayStat) => {
    try {
      const filterObj: QueryParams = {
        page: page,
        pageSize: meta.pageSize,
        stat: stat,
        username: search,
      }
      const data = await userService.readUsers(filterObj)
      setUsers(data.data)
      setMeta(data.meta)
      setIsPageLoading(false)
    } catch (err) {
      console.log("Error fetching users: ", err)
      HelperService.showNotfyErr(notyf, err, "Error fetching users.")
    }
  }

  // Methods
  const handleSelectStat = (selectedOption: any) => {
    if (!selectedOption) {
      return
    }
    setDisplayStat(selectedOption.value)
    fetchUsers(1, selectedOption.value)
  }

  const handleInputChange = (value: string) => {
    setSearch(value);
  };

  // Hooks
  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      fetchUsers(1, displayStat);
      initialRender.current = false;
    } else {
      fetchUsers(1, displayStat);
    }
  }, [debouncedSearch, displayStat]);

  // Table Data
  const columns: Column<User>[] = useMemo(
    () => [
      {
        Header: "Player",
        accessor: 'username',
        Cell: ({ row }) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={row.original.profileImage || placeholderImage}
              alt={row.original.username}
              style={{ width: '30px', height: '30px', marginRight: '10px' }}
            />
            {row.original.isPremium && (
              <FontAwesomeIcon
                icon={faCrown}
                style={{ marginRight: '5px', color: 'gold' }}
              />
            )}
            <span style={{ color: row.original.isPremium ? 'gold' : 'inherit' }}>
              {row.original.username}
            </span>
          </div>
        ),
      },
      {
        Header: "Goals",
        accessor: (row) => row.stats?.goals ?? 0,
      },
      {
        Header: "Assists",
        accessor: (row) => row.stats?.assists ?? 0,
      },
      {
        Header: "Passes",
        accessor: (row) => row.stats?.passes ?? 0,
      },
      {
        Header: "Tackles",
        accessor: (row) => row.stats?.tackles ?? 0,
      },
      {
        Header: "Ints",
        accessor: (row) => row.stats?.interceptions ?? 0,
      },
      {
        Header: "Headers",
        accessor: (row) => row.stats?.headers ?? 0,
      },
      {
        Header: "Saves",
        accessor: (row) => row.stats?.saves ?? 0,
      },
      {
        Header: "Sheets",
        accessor: (row) => row.stats?.cleanSheets ?? 0,
      },
        {
            Header: "PA",
            accessor: (row) => row.stats?.passesAttempted ?? 0,
        },
        {
            Header: "PC",
            accessor: (row) => row.stats?.passesCompleted ?? 0,
        },
        {
            Header: "TA",
            accessor: (row) => row.stats?.tacklesAttempted ?? 0,
        },
        {
            Header: "TC",
            accessor: (row) => row.stats?.tacklesCompleted ?? 0,
        },
        {
            Header: "MR",
            accessor: (row) => row.stats?.matchRating ?? 0,
        },
        {
            Header: "Shots",
            accessor: (row) => row.stats?.shots ?? 0,
        },
    ],
    [users]
  )
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: users });

  // Pagination
  const [meta, setMeta] = useState<Meta>({
    totalPages: 0,
    page: 1,
    pageSize: 25,
  })

  return (
    <>
      {isPageLoading ? (
        <div className='d-flex justify-content-center align-items-center py-5'>
          <LoadingSpinner />
        </div>
      ) : (
        <Container className='container  px-2 px-md-2 body gradient-gray'>
          <div className='row d-flex p-0 m-0'>
            {/* Stat Options */}
            <div className='col-lg-4 col-md-6 col-12 p-0 m-0 mt-2'>
              <Dropdown options={statOptions} onSelect={handleSelectStat} text='Top Goals' />
            </div>

            {/* Spacer - Adjust as per design need */}
            <div className='col-lg-4 col-md-1 col-0'></div>

            {/* Search Bar */}
            <div className='col-lg-4 col-md-5 col-12 p-0 mt-2'>
              <div className='input-container'>
                <input
                  type='text'
                  className='form-control bg-dark text-white'
                  placeholder='Enter player name...'
                  value={search}
                  onChange={(e) => handleInputChange(e.target.value)}
                />
                {search && (
                  <FontAwesomeIcon
                    icon={faTimes}
                    className='text-white clear-icon'
                    onClick={() => setSearch("")}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className='d-flex flex-column mb-3 mt-4'>
            <div className='table-responsive gray-border'>
              <StyledTable {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row)
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </StyledTable>
              {rows.length === 0 && (
                <StyledNoDataFound>No stars in sight! Try adjusting your search.</StyledNoDataFound>
              )}
            </div>
          </div>
          <div className='d-flex justify-content-end'>
            {users.length ? (
              <StyledPagination>
                <ReactPaginate
                  previousLabel={"Previous"}
                  nextLabel={"Next"}
                  pageCount={meta.totalPages}
                  onPageChange={({ selected }) => {
                    fetchUsers(selected + 1)
                  }}
                  containerClassName={"pagination"}
                  pageClassName={"page-item"}
                  pageLinkClassName={"page-link"}
                  previousClassName={"page-item"}
                  nextClassName={"page-item"}
                  previousLinkClassName={"page-link"}
                  nextLinkClassName={"page-link"}
                  activeClassName={"active"}
                />
              </StyledPagination>
            ) : null}
          </div>
        </Container>
      )}
    </>
  )
}

export default LeaderboardTable

const Container = styled.div`
  border-radius: 8px;
  border: 2px solid #5fa8e6;
  min-height: 200px;
  background: linear-gradient(120deg, #000000ff 60%, #040a2cff 100%);
  box-shadow: 0 0 4px 0.5px #00eaff;
`
